import { useRichText } from "@hooks/useRichText";
import { useImage } from "@hooks/useImage";
import { ComponentProps, WithGatsbyImage } from "@ts/components";
import { Content, ProductFeature, SanityKeyed } from "@ts/sanity";
import React, { Dispatch, FC, SetStateAction, useState } from "react";

export type NormalisedFeature = SanityKeyed<
  WithGatsbyImage<ProductFeature, "icon" | "modalIcon" | "modalImage">
> & {
  hasModal: boolean;
};

export type FeaturesInputProps = ComponentProps & {
  description: string;
  features: SanityKeyed<
    WithGatsbyImage<ProductFeature, "icon" | "modalIcon" | "modalImage">
  >[];
  tags: string[];
};

export type FeaturesOutputProps = Omit<
  FeaturesInputProps,
  "tags" | "features"
> & {
  active: boolean;
  activeFeature?: NormalisedFeature;
  content?: Content;
  features: NormalisedFeature[];
  onClick: (key: string) => () => void;
  setActive: Dispatch<SetStateAction<boolean>>;
};

export const withFeatures =
  (Component: FC<FeaturesOutputProps>) =>
  ({ name = "Features", features, tags, description }: FeaturesInputProps) => {
    const { getFluidImage } = useImage();
    const { parseContent } = useRichText();
    const [active, setActive] = useState(false);
    const [activeFeature, setActiveFeature] = useState<NormalisedFeature>();
    const filteredFeatures = features
      ?.filter(({ tags: featureTags }) => {
        return tags.some((tag) =>
          featureTags?.find((featureTag) => featureTag?.value === tag),
        );
      })
      ?.map((feature) => ({
        ...feature,
        hasModal:
          !!feature?.modalContent ||
          !!feature?.modalImage ||
          !!feature?.modalIcon,
      }));
    const featuresWithoutBiomechanicalFootbed = filteredFeatures.filter(
      (feature) =>
        feature.title !== "Biomechanical Footbed" &&
        feature.title !== "Customisable Fit",
    );

    const handleClick = (key: string) => () => {
      const feature = filteredFeatures?.find(({ _key }) => key === _key);

      if (!feature?.hasModal) {
        return;
      }

      setActiveFeature({
        ...feature,
        modalContent: parseContent(feature?.modalContent),
        modalImage: feature?.modalImage
          ? getFluidImage(feature?.modalImage)
          : undefined,
      });
      setActive(true);
    };

    Component.displayName = name;

    if (!features?.length) {
      return null;
    }

    return (
      <Component
        features={featuresWithoutBiomechanicalFootbed}
        description={description}
        active={active}
        setActive={setActive}
        onClick={handleClick}
        activeFeature={activeFeature}
      />
    );
  };
